import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const data = new FormData(formEl);
    const hp = String(data.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      formEl.reset();
      return;
    }
    data.delete("website_alt");
    setStatus("loading");
    setError("");
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dalp4k44ns3ag3t2kecg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const msg = parsed?.meta?.message || parsed?.meta?.detail || text || "";
      if (res.ok && parsed?.code === "OK" && !String(msg).toLowerCase().includes("spam")) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setError(msg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setError("Network error. Please try again.");
    }
  };

  return (
    <footer className="bg-stone mt-0">
      {/* Newsletter block — brand anchor */}
      <section className="brand-satin">
        <div className="shell py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-6">
              <p className="eyebrow mb-4" style={{color:'rgba(253,253,253,0.7)'}}>Newsletter</p>
              <h3 className="font-display text-4xl md:text-5xl leading-tight text-canvas">
                Quiet updates. New releases and slow journal entries.
              </h3>
              <div className="brand-hairline mt-8 max-w-[140px]"></div>
            </div>
            <div className="md:col-span-6">
              {status === "success" ? (
                <p className="text-canvas text-[15px] transition-opacity duration-200" style={{ minHeight: 44 }}>Thank you for joining.</p>
              ) : (
              <form
                id="newsletter-form"
                data-readdy-form
                onSubmit={onSubmit}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Your email address"
                  className="field w-full sm:flex-1 shrink-0 !bg-canvas/95 !border-white/10 focus:!border-brass"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn shrink-0 w-full sm:w-auto min-h-[44px] bg-brass text-canvas hover:brightness-110"
                >
                  {status === "loading" ? "Joining…" : "Subscribe"}
                </button>
              </form>
              )}
              {status === "error" && <p className="mt-4 text-sm text-red-300">{error}</p>}
            </div>
          </div>
        </div>
      </section>

      <div className="shell pt-10 md:pt-24 pb-10 md:pb-20">
        <div className="md:hidden mb-8">
          <p className="font-display text-xl tracking-[0.22em] text-brass">AURELIN</p>
          <p className="mt-3 text-[12px] text-graphite max-w-xs leading-relaxed">
            Contemporary fine jewelry, responsibly crafted in Antwerp.
          </p>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-12 gap-x-4 gap-y-8 md:gap-12">
          <div className="hidden md:block md:col-span-4">
            <p className="font-display text-2xl tracking-[0.24em] text-brass">AURELIN</p>
            <p className="mt-6 text-sm text-graphite max-w-xs leading-relaxed">
              Contemporary fine jewelry, responsibly crafted in small runs from a studio in Antwerp.
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-3 md:mb-6 text-[10px] md:text-[12px]">Shop</p>
            <ul className="space-y-2 md:space-y-4 text-[12px] md:text-sm text-black">
              <li><Link to="/shop" className="hover:text-brass">Shop All</Link></li>
              <li><Link to="/shop?category=Earrings" className="hover:text-brass">Earrings</Link></li>
              <li><Link to="/shop?category=Necklaces" className="hover:text-brass">Necklaces</Link></li>
              <li><Link to="/shop?category=Rings" className="hover:text-brass">Rings</Link></li>
              <li><Link to="/shop?category=Bracelets" className="hover:text-brass">Bracelets</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-3 md:mb-6 text-[10px] md:text-[12px]">Care</p>
            <ul className="space-y-2 md:space-y-4 text-[12px] md:text-sm text-black">
              <li><Link to="/shipping" className="hover:text-brass">Shipping &amp; Returns</Link></li>
              <li><Link to="/care" className="hover:text-brass">Care Guide</Link></li>
              <li><Link to="/faq" className="hover:text-brass">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-brass">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-3 md:mb-6 text-[10px] md:text-[12px]">About</p>
            <ul className="space-y-2 md:space-y-4 text-[12px] md:text-sm text-black">
              <li><Link to="/about#story" className="hover:text-brass">Our Story</Link></li>
              <li><Link to="/about#materials" className="hover:text-brass">Materials</Link></li>
              <li><Link to="/journal" className="hover:text-brass">Journal</Link></li>
            </ul>
          </div>

          <div className="col-span-3 md:col-span-2">
            <p className="eyebrow mb-3 md:mb-6 text-[10px] md:text-[12px]">Social</p>
            <ul className="flex md:block gap-5 md:gap-0 md:space-y-4 space-y-0 text-[12px] md:text-sm text-black">
              <li><a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-brass">Instagram</a></li>
              <li><a href="https://pinterest.com" target="_blank" rel="noreferrer" className="hover:text-brass">Pinterest</a></li>
            </ul>
            <p className="hidden md:block eyebrow mt-8 mb-6">Legal</p>
            <ul className="hidden md:block space-y-4 text-sm text-black">
              <li><Link to="/privacy" className="hover:text-brass">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-brass">Terms</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-8 md:mt-16 pt-6 md:pt-8 border-t border-black/10 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4 text-[10px] md:text-xs text-graphite">
          <p className="text-center sm:text-left">© 2026 Aurelin Maison. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 md:gap-6">
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
            <Link to="/shipping" className="hover:text-black">Shipping &amp; Returns</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}